import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { LogEvent } from "../lib/types";
import { invokeOps, errMsg } from "../lib/adminApi";
import { getPipelineSnapshot } from "../lib/pipelineSnapshot";
import { Banner, TabButton } from "../components/ui";

type Tab = "cloudwatch" | "sentry";

// Where the Sentry tab links to. Set VITE_SENTRY_URL in the Pages build env to
// your exact project issues URL (e.g. https://<org>.sentry.io/issues/?project=<id>);
// falls back to the Sentry home if unset.
const SENTRY_URL = import.meta.env.VITE_SENTRY_URL ?? "https://sentry.io/";

export default function LogsPage() {
  // Tab lives in the URL (?tab=) so back/refresh/deep-links preserve it (ADM-12).
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get("tab") === "sentry" ? "sentry" : "cloudwatch";
  const setTab = (t: Tab) => {
    const next = new URLSearchParams(params);
    if (t === "cloudwatch") next.delete("tab");
    else next.set("tab", t);
    setParams(next, { replace: true });
  };

  return (
    <div>
      <h1 className="text-xl font-semibold">Logs &amp; Errors</h1>
      <div className="mt-4 flex gap-2 border-b border-zinc-800">
        <TabButton active={tab === "cloudwatch"} onClick={() => setTab("cloudwatch")}>
          CloudWatch
        </TabButton>
        <TabButton active={tab === "sentry"} onClick={() => setTab("sentry")}>
          Sentry
        </TabButton>
      </div>
      {/* Keep CloudWatch mounted (just hidden) so its fetched logs + selection
          survive a hop to the Sentry tab and back (ADM-12). */}
      <div className={tab === "cloudwatch" ? "mt-4" : "mt-4 hidden"}>
        <CloudWatchTab />
      </div>
      {tab === "sentry" && (
        <div className="mt-4">
          <SentryTab />
        </div>
      )}
    </div>
  );
}

function CloudWatchTab() {
  // Deep-link (§6, D2): a Lambda node links here as /logs?group=/aws/lambda/<name>
  // (+ optional ?q=<filter>). When `group` is present we preselect it and
  // auto-fetch on mount; otherwise the manual picker/typeahead UX is unchanged.
  const [searchParams] = useSearchParams();
  const deepLinkGroup = searchParams.get("group") ?? "";
  const deepLinkQuery = searchParams.get("q") ?? "";

  const [logGroups, setLogGroups] = useState<string[]>([]);
  const [groupsError, setGroupsError] = useState<string | null>(null);
  const [logGroup, setLogGroup] = useState(deepLinkGroup);
  const [events, setEvents] = useState<LogEvent[]>([]);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Populate the picker from the pipeline snapshot — the ops Lambda already
  // enumerates every function via ListFunctions, so the list never goes stale as
  // functions come and go (vs. a hardcoded list). Served from the shared
  // short-TTL cache so this doesn't re-fire the heavy snapshot on every mount /
  // drill-down (ADM-3) — a prior Pipeline visit warms it for free.
  useEffect(() => {
    let cancelled = false;
    getPipelineSnapshot()
      .then((snap) => {
        if (cancelled) return;
        setLogGroups(
          snap.lambdas
            .map((l) => `/aws/lambda/${l.name}`)
            .sort((a, b) => a.localeCompare(b)),
        );
      })
      .catch((e) => {
        if (!cancelled) setGroupsError(errMsg(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const runFetch = useCallback(
    async (group: string, append = false, token?: string) => {
      if (!group.trim()) return;
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ logGroup: group.trim(), limit: "50" });
        if (token) params.set("nextToken", token);
        // Optional deep-link filter (?q=) — passed through to CloudWatch's
        // FilterLogEvents pattern; harmless if the ops Lambda ignores it.
        if (deepLinkQuery.trim()) params.set("filterPattern", deepLinkQuery.trim());
        const data = await invokeOps<{ events: LogEvent[]; nextToken?: string }>(
          `/logs/cloudwatch?${params}`,
        );
        setEvents((prev) => (append ? [...prev, ...data.events] : data.events));
        setNextToken(data.nextToken);
      } catch (e) {
        setError(errMsg(e));
      } finally {
        setLoading(false);
      }
    },
    [deepLinkQuery],
  );

  const fetchFresh = useCallback(() => runFetch(logGroup), [logGroup, runFetch]);

  // Deep-link auto-fetch (§6): if we arrived with ?group=…, fetch it once on
  // mount. Keyed only on the deep-link value so it never re-fires as the user
  // changes the selection manually afterward.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot deep-link fetch; loader's sync setLoading/setError reset is intentional (shared with Refresh); data state is only written after the awaited request
    if (deepLinkGroup.trim()) runFetch(deepLinkGroup);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkGroup]);

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        fetchFresh();
      }, 30_000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefresh, fetchFresh]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={logGroups.includes(logGroup) ? logGroup : ""}
          onChange={(e) => {
            setLogGroup(e.target.value);
            if (e.target.value) runFetch(e.target.value);
          }}
          className="flex-1 min-w-[16rem] rounded border border-zinc-700 bg-zinc-800 px-3 py-1.5 text-sm text-white focus:outline-none focus:ring-1 focus:ring-zinc-500"
        >
          <option value="">
            {groupsError
              ? "Couldn't load functions — type a group below"
              : logGroups.length
                ? "Select a Lambda log group…"
                : "Loading functions…"}
          </option>
          {logGroups.map((g) => (
            <option key={g} value={g}>
              {g.replace("/aws/lambda/", "")}
            </option>
          ))}
        </select>
        <button
          onClick={fetchFresh}
          disabled={loading || !logGroup.trim()}
          className="rounded bg-zinc-700 px-3 py-1.5 text-sm text-white hover:bg-zinc-600 disabled:opacity-50"
        >
          {loading ? "Loading…" : "Fetch"}
        </button>
        <label className="flex items-center gap-1.5 text-sm text-zinc-400 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
            className="rounded"
          />
          Auto-refresh (30s)
        </label>
      </div>

      {/* Power-user escape hatch: type any /aws/lambda/* group not in the list. */}
      <input
        type="text"
        value={logGroup}
        onChange={(e) => setLogGroup(e.target.value)}
        placeholder="…or type a log group: /aws/lambda/function-name"
        className="mt-2 w-full rounded border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 placeholder-zinc-600 focus:outline-none focus:ring-1 focus:ring-zinc-600"
        onKeyDown={(e) => e.key === "Enter" && fetchFresh()}
      />

      {groupsError && (
        <p className="mt-2 text-xs text-amber-500">
          Function list unavailable ({groupsError}) — you can still type a log group above.
        </p>
      )}

      {error && <Banner className="mt-3">{error}</Banner>}

      {events.length > 0 && (
        <div className="mt-4 rounded-lg border border-zinc-800 bg-zinc-900 max-h-[60vh] overflow-y-auto">
          <div className="divide-y divide-zinc-800">
            {events.map((ev, i) => (
              <div key={i} className="px-3 py-2">
                <div className="flex items-baseline gap-3 text-xs text-zinc-500 mb-0.5">
                  <span>{ev.timestamp ? new Date(ev.timestamp).toLocaleString() : "—"}</span>
                  {ev.logStreamName && <span className="truncate">{ev.logStreamName}</span>}
                </div>
                <pre className="text-xs text-zinc-300 whitespace-pre-wrap break-all font-mono">
                  {ev.message}
                </pre>
              </div>
            ))}
          </div>
        </div>
      )}

      {events.length === 0 && !loading && !error && (
        <p className="mt-6 text-sm text-zinc-500">Pick a log group to view its recent events.</p>
      )}

      {nextToken && (
        <button
          onClick={() => runFetch(logGroup, true, nextToken)}
          disabled={loading}
          className="mt-3 rounded bg-zinc-700 px-3 py-1.5 text-sm text-white hover:bg-zinc-600 disabled:opacity-50"
        >
          {loading ? "Loading…" : "Load more"}
        </button>
      )}
    </div>
  );
}

// Sentry has its own (far richer) UI — stack traces, breadcrumbs, trends, alerts.
// Rather than maintain a thin in-console mirror (and a server token to read the
// API), just link out.
function SentryTab() {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-6 text-sm text-zinc-400">
      <p>
        Errors and performance are tracked in <span className="text-zinc-200">Sentry</span>.
        Open it for full stack traces, breadcrumbs, release health, and alerts.
      </p>
      <a
        href={SENTRY_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="mt-3 inline-block rounded bg-zinc-100 px-3 py-1.5 text-sm font-medium text-zinc-900 hover:bg-white"
      >
        Open Sentry →
      </a>
    </div>
  );
}
